import { useState, useRef, useCallback } from "react";

const API_URL = "/api";

const CLASS_META = {
  Good_Cucumber: {
    icon: "check_circle",
    color: "text-growth-green",
    bg: "bg-growth-green",
    light: "bg-green-50 border-growth-green",
    label: "Good Cucumber",
    desc: "High-quality seed with strong germination potential",
  },
  Bad_Cucumber: {
    icon: "cancel",
    color: "text-red-500",
    bg: "bg-red-500",
    light: "bg-red-50 border-red-400",
    label: "Bad Cucumber",
    desc: "Seed shows signs of defects or low viability",
  },
  Other_Crop: {
    icon: "grass",
    color: "text-earth-clay",
    bg: "bg-earth-clay",
    light: "bg-amber-50 border-earth-clay",
    label: "Other Crop",
    desc: "Image contains a non-cucumber crop or plant",
  },
  Invalid_Image: {
    icon: "hide_image",
    color: "text-outline",
    bg: "bg-outline",
    light: "bg-gray-50 border-outline",
    label: "Invalid Image",
    desc: "Image is not suitable for seed quality analysis",
  },
};

export default function Models() {
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const handleFile = useCallback((f) => {
    if (!f || !f.type.startsWith("image/")) return;
    setFile(f);
    setResult(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      handleFile(e.dataTransfer.files[0]);
    },
    [handleFile]
  );

  const onPredict = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_URL}/predict`, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError(err.message.includes("Failed to fetch")
        ? "Cannot reach the API server. Please ensure the backend is running."
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const topClass = result ? CLASS_META[result.prediction] : null;
  const confidence = result ? Math.round(result.confidence * 100) : 0;

  return (
    <>
      {/* Hero */}
      <section className="relative py-20 md:py-28 px-gutter bg-data-teal text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg">
            <circle cx="700" cy="50" r="300" fill="none" stroke="white" strokeWidth="1" />
            <circle cx="700" cy="50" r="200" fill="none" stroke="white" strokeWidth="0.5" />
            <circle cx="100" cy="350" r="200" fill="none" stroke="white" strokeWidth="0.5" />
          </svg>
        </div>
        <div className="max-w-container-max mx-auto relative z-10 grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-1 rounded-full border border-white/20">
              <span className="material-symbols-outlined text-cucumber-blossom text-sm">model_training</span>
              <span className="font-label-mono text-label-mono text-cucumber-blossom uppercase">AI-Powered Seed Quality</span>
            </div>
            <h1 className="font-headline-xl text-headline-xl leading-tight">
              Cucumber Seed <span className="text-primary-fixed">Quality</span> Classifier
            </h1>
            <p className="font-body-lg text-body-lg text-surface-variant max-w-lg">
              Upload a seed image and our deep learning model will instantly classify it — identifying quality, defects, and crop type with scientific precision.
            </p>
            <div className="flex flex-wrap gap-6 pt-2">
              {["4 Quality Classes", "Scientifically Validated"].map((tag) => (
                <div key={tag} className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-cucumber-blossom text-sm">check_circle</span>
                  <span className="font-label-bold text-label-bold uppercase tracking-wider">{tag}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Model Architecture Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 space-y-6 border border-white/20">
              <h3 className="font-label-bold text-label-bold uppercase tracking-widest text-cucumber-blossom">About This Model</h3>
              <p className="font-body-md text-body-md text-white">Upload a cucumber seed image and get an instant AI-powered quality assessment — identifying good seeds, defective seeds, other crops, and invalid images.</p>
            </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="py-20 px-gutter max-w-container-max mx-auto">
        <div className="text-center mb-12 space-y-3">
          <span className="font-label-bold text-label-bold text-primary uppercase tracking-widest">Live Demo</span>
          <h2 className="font-headline-lg text-headline-lg text-on-background">Test the Model</h2>
          <p className="font-body-md text-body-md text-on-surface-variant max-w-xl mx-auto">
            Upload any cucumber seed image and get an instant AI-powered quality assessment.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Upload Panel */}
          <div className="space-y-4">
            <div
              className={`relative rounded-2xl border-2 border-dashed transition-all cursor-pointer ${
                dragOver
                  ? "border-primary bg-primary/5 scale-[1.01]"
                  : preview
                  ? "border-outline-variant bg-surface-container-low"
                  : "border-outline-variant bg-surface-container-low hover:border-primary hover:bg-primary/5"
              }`}
              style={{ minHeight: "320px" }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => !preview && inputRef.current.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
              />
              {preview ? (
                <div className="relative w-full h-full">
                  <img src={preview} alt="preview" className="w-full rounded-2xl object-contain max-h-80" />
                  <button
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-md hover:bg-white transition-colors"
                  >
                    <span className="material-symbols-outlined text-on-surface text-xl">close</span>
                  </button>
                  <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5">
                    <span className="font-label-mono text-label-mono text-on-surface truncate max-w-[200px] block">{file?.name}</span>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="material-symbols-outlined text-5xl text-primary">add_photo_alternate</span>
                  </div>
                  <div className="text-center space-y-1">
                    <p className="font-headline-md text-headline-md text-on-background">Drop your image here</p>
                    <p className="font-body-sm text-body-sm text-on-surface-variant">or click to browse · JPG, PNG, WEBP</p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onPredict}
                disabled={!file || loading}
                className={`flex-1 h-12 rounded-lg font-label-bold text-label-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${
                  file && !loading
                    ? "bg-primary text-on-primary shadow-lg shadow-primary/20 hover:brightness-110"
                    : "bg-surface-container text-on-surface-variant cursor-not-allowed"
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">biotech</span>
                    Run Analysis
                  </>
                )}
              </button>
              {file && (
                <button
                  onClick={reset}
                  className="h-12 px-4 rounded-lg border-2 border-outline-variant text-on-surface-variant hover:border-primary hover:text-primary transition-colors"
                >
                  <span className="material-symbols-outlined">refresh</span>
                </button>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-3 bg-error-container text-on-error-container rounded-lg px-4 py-3">
                <span className="material-symbols-outlined text-lg mt-0.5">error</span>
                <p className="font-body-sm text-body-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Results Panel */}
          <div className="space-y-4">
            {!result && !loading && (
              <div className="rounded-2xl border border-outline-variant bg-surface-container-low p-10 flex flex-col items-center justify-center gap-4 text-center" style={{ minHeight: "320px" }}>
                <div className="w-16 h-16 rounded-full bg-surface-container flex items-center justify-center">
                  <span className="material-symbols-outlined text-4xl text-outline">analytics</span>
                </div>
                <p className="font-headline-md text-headline-md text-on-surface-variant">Results will appear here</p>
                <p className="font-body-sm text-body-sm text-outline max-w-xs">Upload an image and click Run Analysis to see the AI classification results</p>
              </div>
            )}

            {loading && (
              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-10 flex flex-col items-center justify-center gap-6" style={{ minHeight: "320px" }}>
                <div className="relative w-20 h-20">
                  <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-t-primary animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary text-2xl">memory</span>
                  </div>
                </div>
                <div className="text-center space-y-1">
                  <p className="font-headline-md text-headline-md text-on-background">Analyzing Image</p>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">Processing your image...</p>
                </div>
              </div>
            )}

            {result && topClass && (
              <div className="space-y-4">
                {/* Top Prediction */}
                <div className={`rounded-2xl border-2 p-6 ${topClass.light}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl ${topClass.bg} flex items-center justify-center shadow-lg`}>
                      <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>{topClass.icon}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-label-bold text-label-bold text-on-surface-variant uppercase tracking-widest mb-1">Prediction</p>
                      <h3 className={`font-headline-md text-headline-md ${topClass.color}`}>{topClass.label}</h3>
                    </div>
                    <div className="text-right">
                      <p className="font-label-bold text-label-bold text-on-surface-variant uppercase tracking-widest mb-1">Confidence</p>
                      <p className={`font-headline-lg text-headline-lg ${topClass.color}`}>{confidence}%</p>
                    </div>
                  </div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">{topClass.desc}</p>
                  {/* Confidence bar */}
                  <div className="mt-4">
                    <div className="h-2 w-full bg-white/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${topClass.bg} transition-all duration-700 rounded-full`}
                        style={{ width: `${confidence}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* All Probabilities */}
                <div className="rounded-2xl border border-outline-variant bg-white p-6 space-y-4">
                  <h4 className="font-label-bold text-label-bold uppercase tracking-widest text-on-surface-variant">All Class Probabilities</h4>
                  {Object.entries(result.all_probabilities)
                    .sort(([, a], [, b]) => b - a)
                    .map(([cls, prob]) => {
                      const meta = CLASS_META[cls];
                      const pct = Math.round(prob * 100 * 10) / 10;
                      const isTop = cls === result.prediction;
                      return (
                        <div key={cls} className={`space-y-1 p-3 rounded-lg ${isTop ? "bg-surface-container-low" : ""}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`material-symbols-outlined text-sm ${meta.color}`} style={{ fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
                              <span className={`font-label-bold text-label-bold ${isTop ? meta.color : "text-on-surface-variant"}`}>{meta.label}</span>
                              {isTop && <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full font-label-bold text-[10px] uppercase tracking-wider">Top</span>}
                            </div>
                            <span className={`font-label-mono text-label-mono ${isTop ? meta.color : "text-on-surface-variant"}`}>{pct}%</span>
                          </div>
                          <div className="h-1.5 w-full bg-surface-container rounded-full overflow-hidden">
                            <div
                              className={`h-full ${meta.bg} transition-all duration-700 rounded-full`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>



      {/* Classes Reference */}
      <section className="py-20 px-gutter max-w-container-max mx-auto">
        <div className="text-center mb-14 space-y-3">
          <span className="font-label-bold text-label-bold text-primary uppercase tracking-widest">Reference</span>
          <h2 className="font-headline-lg text-headline-lg text-on-background">Classification Classes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(CLASS_META).map(([key, meta]) => (
            <div key={key} className={`rounded-xl border-2 p-6 flex gap-5 ${meta.light}`}>
              <div className={`w-14 h-14 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
              </div>
              <div className="space-y-1">
                <h3 className={`font-headline-md text-headline-md ${meta.color}`}>{meta.label}</h3>
                <p className="font-body-sm text-body-sm text-on-surface-variant">{meta.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-gutter">
        <div className="max-w-container-max mx-auto bg-on-background rounded-3xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 800 300"><circle cx="400" cy="150" r="250" fill="none" stroke="white" strokeWidth="1"/><circle cx="400" cy="150" r="180" fill="none" stroke="white" strokeWidth="0.5"/></svg>
          </div>
          <div className="relative z-10 space-y-6">
            <h2 className="font-headline-lg text-headline-lg text-white">Access our Research Database</h2>
            <p className="font-body-md text-body-md text-surface-variant opacity-80 max-w-xl mx-auto">
              All CAFE model results are validated in collaboration with IIT Ropar. Explore our full research papers and seed standards.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <button className="bg-primary text-on-primary h-12 px-8 rounded-lg font-label-bold text-label-bold uppercase tracking-widest hover:brightness-110 transition-all shadow-lg">
                View Research Papers
              </button>
              <button className="border-2 border-white/30 text-white h-12 px-8 rounded-lg font-label-bold text-label-bold uppercase tracking-widest hover:bg-white/10 transition-all">
                Request Demo
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
